/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { NgClass } from "@angular/common";
import { Component, EventEmitter, forwardRef, Input, Output } from "@angular/core";
import {
	AbstractControl,
	ControlValueAccessor,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	ValidationErrors,
	Validator
} from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { INPUT_FLOAT_REGEXP, INPUT_INTEGER_REGEXP } from "../../utils/constants";

@Component({
	selector: "globalictcc-input",
	standalone: true,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => InputComponent),
			multi: true
		},
		{
			provide: NG_VALIDATORS,
			useExisting: forwardRef(() => InputComponent),
			multi: true
		}
	],
	imports: [MatFormFieldModule, MatInputModule, NgClass],
	templateUrl: "./input.component.html",
	styleUrl: "./input.component.scss"
})
export class InputComponent implements ControlValueAccessor, Validator {
	@Input() public label: string = "";
	@Input() public placeholder: string = "";
	@Input() public iconName: string = "";
	@Input() public disabled: boolean = false;
	@Input() public unit: string = "";
	@Input() public inputType: string = "text";
	@Input() public min: number | null = null;
	@Input() public max: number | null = null;
	@Input() public step: number | null = null;
	@Input() public shouldRoundValue: boolean = true;
	@Input() public customErrorMessage: string = "";
	@Input() public acceptFloat: boolean = true;

	@Output() public handleInputChange = new EventEmitter<string | number | null>();

	public value: string | number | null = "";
	public isDisabled: boolean = false;
	public isInvalid: boolean = false;

	/**
	 * Handles the input change event for the component.
	 *
	 * @param event - The input event triggered by the user.
	 *
	 * This method performs the following actions:
	 * - Casts the event target to an HTMLInputElement.
	 * - Formats the input value using a private `_formatInput` method.
	 * - Updates the input field's value with the formatted value.
	 * - Emits the formatted value using the `handleInputChange` event emitter.
	 * - Calls the `_onChange` callback to propagate the value to the model.
	 * - Marks the input as touched by calling `onTouched`.
	 * - Triggers validation by invoking `_onValidatorChange`.
	 */
	public onInputChange(event: Event) {
		const target = event.target as HTMLInputElement;

		if (this.inputType === "number") {
			const match = target.value.match(
				this.acceptFloat ? INPUT_FLOAT_REGEXP : INPUT_INTEGER_REGEXP
			);

			this.value = match ? match[0] : "";
			target.value = this.value;
		}

		this.value = target.value;

		this.handleInputChange.emit(
			this.inputType === "number" ? parseFloat(this.value) : this.value
		);

		this._onChange(this.value);
		this.onTouched();
		this._onValidatorChange();
	}

	/**
	 * Writes a value to the component.
	 *
	 * @param value - The value to be written. It can be a string or null.
	 */
	public writeValue(value: string | number | null) {
		this.value = value;
	}

	/**
	 * Sets the disabled state of the input component.
	 *
	 * @param isDisabled - A boolean value indicating whether the input should be disabled.
	 */
	public setDisabledState(isDisabled: boolean) {
		this.isDisabled = isDisabled;
	}

	/**
	 * A private method that serves as a placeholder for a change event handler.
	 * This method is intended to be replaced with a custom implementation.
	 *
	 * @param value - The new value of the input, which can be a string or null.
	 */
	private _onChange: (value: string | number | null) => void = () => {};

	/**
	 * Registers a callback function that is called when the value changes.
	 *
	 * @param fn - The callback function that is called with the new value.
	 */
	public registerOnChange(fn: (value: string | number | null) => void) {
		this._onChange = fn;
	}

	/**
	 * A callback function that is called when the input component is touched.
	 * This function is typically used in forms to mark the control as "touched".
	 * It is initialized as a no-op function.
	 */
	public onTouched: () => void = () => {};

	/**
	 * Registers a callback function that will be called when the input element is touched.
	 * This method is typically used by Angular forms to register a handler for touch events.
	 *
	 * @param fn - The callback function to be registered.
	 */
	public registerOnTouched(fn: () => void) {
		this.onTouched = fn;
	}

	/**
	 * A private method that serves as a placeholder for a function that will be called
	 * when the validator changes. This method is initially set to an empty function.
	 *
	 * @private
	 */
	private _onValidatorChange = () => {};

	/**
	 * Registers a function to be called when the validator changes.
	 *
	 * @param fn - The function to be called when the validator changes.
	 */
	public registerOnValidatorChange(fn: () => void) {
		this._onValidatorChange = fn;
	}

	/**
	 * Validates the given form control and updates the `isInvalid` property.
	 *
	 * @param control - The form control to validate.
	 * @returns An object containing validation errors if any, otherwise null.
	 */
	public validate(control: AbstractControl): ValidationErrors | null {
		this.isInvalid = !!control.errors && (control.touched || control.dirty);

		return null;
	}
}
